//
// Copyright (c) 2026 Comcast
//

import { createSlice, isFulfilled } from '@reduxjs/toolkit'
import listenerMiddleware from 'listenerMiddleware'
import { locationChanged } from 'reducers/routing'

import * as modalFormSelector from 'selectors/modalForm'

import {
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
} from 'constants/notification'

const NOTIFICATION_DISMISS_DELAY_MS = 4000

const initialState = {
  text: null,
  type: NOTIFICATION_TYPE_SUCCESS,
  hideRefreshLink: false,
  notificationId: null,
  actionButtons: [],
  shouldClearOnLocationChange: false,
}

export const notificationSlice = createSlice({
  name: 'notification',
  initialState,

  reducers: {
    addNotification: (state, action) => {
      const payload = action.payload
      state.text = payload.text || null
      state.type = payload.type || NOTIFICATION_TYPE_SUCCESS
      state.hideRefreshLink = !!payload.hideRefreshLink
      state.notificationId = payload.notificationId || null
      state.actionButtons = payload.actionButtons || []
      state.shouldClearOnLocationChange = payload.shouldClearOnLocationChange || false
    },

    clearNotification: (state, action) => {
      return initialState
    },

    notificationButtonClick: (state, action) => {
    },
  },

  extraReducers: (builder) => {
    builder
      // clear notification on page change if required
      .addCase(locationChanged, (state, action) => {
        return state.shouldClearOnLocationChange ? initialState : state
      })
  },
})

export const {
  addNotification,
  clearNotification,
  notificationButtonClick,
} = notificationSlice.actions

export default notificationSlice.reducer


// Listeners

listenerMiddleware.startListening({
  matcher: (action) => {
    return Boolean(action?.payload?.message?.text)
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()
    const isModalVisible = modalFormSelector.isVisible(state)
    const isModalOperationActive = modalFormSelector.operationActive(state)
    const showInModal = isModalVisible && isModalOperationActive && action.payload.error

    if (!showInModal) {
      const { text, type, isPersistent, hideRefreshLink } = action.payload.message
      const shouldClearOnLocationChange = Boolean(type === NOTIFICATION_TYPE_ERROR && isFulfilled(action))

      listenerApi.dispatch(addNotification({
        text,
        type,
        isPersistent,
        hideRefreshLink,
        shouldClearOnLocationChange,
      }))
    }
  },
})

let dismissTimer = null

// auto-dismiss notification after delay
listenerMiddleware.startListening({
  actionCreator: addNotification,
  effect: (action, listenerApi) => {
    // a timer left from the previous notification must not clear the new one
    if (dismissTimer) {
      clearTimeout(dismissTimer)
      dismissTimer = null
    }

    const { type, isPersistent } = action.payload
    const needAutoDismissis = Boolean(type !== NOTIFICATION_TYPE_ERROR && !isPersistent)
    if (!needAutoDismissis) {
      return
    }

    dismissTimer = setTimeout(() => {
      listenerApi.dispatch(clearNotification())
    }, NOTIFICATION_DISMISS_DELAY_MS)
  },
})

listenerMiddleware.startListening({
  actionCreator: clearNotification,
  effect: (action) => {
    if (dismissTimer) {
      clearTimeout(dismissTimer)
      dismissTimer = null
    }
  },
})
